import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExportDownload, getMe, type Me } from '../api/endpoints';
import Loading from '../components/Loading';
import Toast from '../components/ui/Toast';
import { getErrorMessage } from '../utils/errors';

const DownloadIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const ShieldIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const AlertIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

export default function DataExportDownloadPage(): React.JSX.Element {
    const { exportId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [user, setUser] = useState<Me | null>(null);
    const [toast, setToast] = useState<{
        open: boolean;
        variant: 'success' | 'error' | 'info';
        title: string;
        description?: string;
    }>({ open: false, variant: 'info', title: '' });

    useEffect(() => {
        async function init() {
            try {
                const u = await getMe();
                setUser(u);
            } catch {
                const next = encodeURIComponent(window.location.pathname);
                navigate(`/login?next=${next}`);
            } finally {
                setLoading(false);
            }
        }
        void init();
    }, [navigate]);

    const handleDownload = async () => {
        if (!user || !exportId) return;
        setDownloading(true);
        try {
            const blob = await getExportDownload(user.id, exportId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kivo-data-export-${exportId.slice(0, 8)}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setToast({
                open: true,
                variant: 'success',
                title: 'Download started',
                description: 'Your data archive is being downloaded.'
            });
        } catch (error_: unknown) {
            setToast({
                open: true,
                variant: 'error',
                title: 'Download failed',
                description: getErrorMessage(error_, 'The archive may have expired or you may not have permission.')
            });
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <Loading />;

    const emailLocal = user?.email.split('@')[0];
    const displayName = user?.name ?? emailLocal;

    return (
        <div style={{
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--s-5)',
        }}>
            <Toast
                open={toast.open}
                onOpenChange={(open) => { setToast(prev => ({ ...prev, open })); }}
                variant={toast.variant}
                title={toast.title}
                description={toast.description}
                autoCloseMs={5000}
            />

            <div className="compliance-card" style={{ maxWidth: 520, width: '100%' }}>
                <div className="stack-6" style={{ textAlign: 'center' }}>

                    <div className="icon-ring" style={{ margin: '0 auto' }}>
                        <DownloadIcon size={40} />
                    </div>

                    <div className="stack-3">
                        <h1 className="t-h1">Data archive <em className="t-italic">ready</em></h1>
                        <p className="t-body t-mute">
                            Hi {displayName}, your personal data export is compiled and ready for secure download.
                        </p>
                    </div>

                    <div
                        className="card card--sand card--bordered stack-4"
                        style={{ textAlign: 'left', padding: 'var(--s-5)' }}
                    >
                        <div className="row-4" style={{ alignItems: 'flex-start' }}>
                            <div className="concern-dot concern-dot--deep">
                                <ShieldIcon size={14} />
                            </div>
                            <div className="stack-2">
                                <p className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', margin: 0 }}>
                                    Verified session
                                </p>
                                <p className="t-small t-mute" style={{ margin: 0 }}>
                                    You are signed in as <strong>{user?.email}</strong>. Only the account owner can retrieve this file.
                                </p>
                            </div>
                        </div>

                        <hr className="rule" />

                        <div className="row-4" style={{ alignItems: 'flex-start' }}>
                            <div className="concern-dot concern-dot--teal">
                                <AlertIcon size={14} />
                            </div>
                            <div className="stack-2">
                                <p className="t-small" style={{ fontWeight: 500, color: 'var(--ink)', margin: 0 }}>
                                    Encrypted archive
                                </p>
                                <p className="t-small t-mute" style={{ margin: 0 }}>
                                    The ZIP is password-protected. Check your notification email for the extraction key.
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        className="btn btn--primary btn--full"
                        onClick={handleDownload}
                        disabled={downloading}
                    >
                        <DownloadIcon size={16} />
                        {downloading ? 'Preparing…' : 'Download archive'}
                    </button>

                    <div
                        className="t-label row-3"
                        style={{ justifyContent: 'center', color: 'var(--ink-muted)' }}
                    >
                        <ShieldIcon size={11} />
                        Encrypted delivery
                    </div>

                </div>
            </div>

            <button
                className="btn btn--ghost btn--sm"
                style={{ marginTop: 'var(--s-5)' }}
                onClick={() => { navigate('/'); }}
            >
                <span>←</span> Return to dashboard
            </button>
        </div>
    );
}
